/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type { OptionIdentifier } from '@/types/optionDiscovery';

export type AppPage =
  | 'btcmarket'
  | 'ethmarket'
  | 'solmarket'
  | 'xrpmarket'
  | 'positions'
  | 'profile'
  | 'settings'
  | 'deposit'
  | 'landing'
  | 'success'
  | 'splash'
  | 'optionshome'
  | 'optionchart';

export interface NavigationContextValue {
  currentPage: AppPage;
  navigate: (page: AppPage) => void;
  selectedOption: OptionIdentifier | null;
  selectOption: (option: OptionIdentifier) => void;
  clearSelectedOption: () => void;
}

const LAST_PAGE_KEY = 'yv_last_page_v1';

function getLastVisitedPage(): AppPage {
  const url = new URL(window.location.href);

  if (window.self !== window.top) {
    return 'optionshome';
  }

  // Check for direct page access via URL path or query parameter
  const pathname = url.pathname;
  const pageParam = url.searchParams.get('page');

  // Handle direct URL paths like /success
  if (pathname === '/success') {
    return 'success';
  }

  // Handle query parameters like ?page=success
  if (pageParam === 'success') {
    return 'success';
  }

  // Check for app access path like /micah
  if (pathname !== '/micah' && pathname !== '/') {
    return 'landing';
  }

  // If pathname is '/', check if it should show landing or app
  // (This allows / to show landing, and /micah to show app)
  if (pathname === '/') {
    return 'landing';
      }

  // For /micah path, always show splash screen first
  if (pathname === '/micah') {
    return 'splash';
  }

  return 'optionshome';
}

export function NavigationProvider({
  initialPage,
  children,
}: {
  initialPage?: AppPage;
  children: React.ReactNode;
}) {
  // Use saved page if no explicit initial page is provided
  const [currentPage, setCurrentPage] = useState<AppPage>(
    initialPage ?? getLastVisitedPage(),
  );
  const [selectedOption, setSelectedOption] = useState<OptionIdentifier | null>(
    null,
  );

  const navigate = useCallback(
    (page: AppPage) => {
      console.log('Navigation called:', page, 'current:', currentPage);
      setCurrentPage(page);

      // Update URL for certain pages to enable direct access
      if (page === 'success') {
        window.history.pushState({}, '', '/success');
      } else if (page === 'landing') {
        window.history.pushState({}, '', '/');
      }
    },
    [currentPage],
  );

  const selectOption = useCallback(
    (option: OptionIdentifier) => {
      setSelectedOption(option);
      navigate('optionchart');
    },
    [navigate],
  );

  const clearSelectedOption = useCallback(() => {
    setSelectedOption(null);
  }, []);

  const value = useMemo(
    () => ({
      currentPage,
      navigate,
      selectedOption,
      selectOption,
      clearSelectedOption,
    }),
    [currentPage, navigate, selectedOption, selectOption, clearSelectedOption],
  );

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
}

export const NavigationContext = createContext<
  NavigationContextValue | undefined
>(undefined);

export function useNavigation(): NavigationContextValue {
  const ctx = useContext(NavigationContext);
  if (!ctx) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return ctx;
}
